import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Box,
  Checkbox,
  Container,
  FormField,
  StatusIndicator,
  Textarea,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { DlqRedriveInfoApiFactory } from "../../lusca-search-api/LuscaSearchApiFactory";
import { generateMcmUrl } from "../../helpers/logUtils";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { StageSelector } from "../redrive/components/StageSelector";
import { FileUploader } from "../redrive/components/FileUploader";
import { useDlqFileValidation } from "./hooks/useDlqFileValidation";
import { useExecuteDlqRedrive } from "./hooks/useExecuteDlqRedrive";
import { useDlqRedriveData } from "./hooks/useDlqRedriveData";
import { useDlqRedriveDetails } from "./hooks/useDlqRedriveDetails";
import { DlqExecutionsTable } from "./table/dlqExecutionsTable";
import { DlqRedriveApprovalFlow } from "./DlqRedriveApprovalFlow";
import { DlqRedriveExecutionView } from "./DlqRedriveExecutionView";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

// Per-stage localStorage key for prepared MCM input, so it survives a tab close
// during the approval gap.
const uploadStorageKey = (stage: Stage) => `lusca-dlq-redrive-upload-${stage}`;

interface PreparedMcm {
  s3Uri?: string;
  etag?: string;
  messageIds?: string[];
  count: number;
  mcmId?: string;
  mcmUrl?: string;
  warning?: string;
}

const parsePreparedMcm = (raw: string): PreparedMcm | null => {
  const parsed: unknown = JSON.parse(raw);
  if (!parsed || typeof parsed !== "object") {
    return null;
  }
  const values = parsed as Record<string, unknown>;
  const hasUpload =
    typeof values.s3Uri === "string" && typeof values.etag === "string";
  const messageIds = Array.isArray(values.messageIds)
    ? values.messageIds.filter(
        (value): value is string => typeof value === "string" && !!value.trim()
      )
    : [];
  if (
    typeof values.count !== "number" ||
    (!hasUpload && messageIds.length === 0)
  ) {
    return null;
  }
  return {
    s3Uri: hasUpload ? (values.s3Uri as string) : undefined,
    etag: hasUpload ? (values.etag as string) : undefined,
    messageIds: messageIds.length > 0 ? messageIds : undefined,
    count: values.count,
    mcmId: typeof values.mcmId === "string" ? values.mcmId : undefined,
    mcmUrl: typeof values.mcmUrl === "string" ? values.mcmUrl : undefined,
    warning: typeof values.warning === "string" ? values.warning : undefined,
  };
};

const getApiErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body: unknown = error.response?.data;
    if (body && typeof body === "object") {
      const errorBody = body as {
        error?: unknown;
        details?: unknown;
        requestId?: unknown;
      };
      const summary =
        typeof errorBody.error === "string" ? errorBody.error : null;
      const details =
        typeof errorBody.details === "string" ? errorBody.details : null;
      const requestId =
        typeof errorBody.requestId === "string" ? errorBody.requestId : null;
      const parts = [summary, details, requestId && `Request ID: ${requestId}`];
      const message = parts.filter(Boolean).join(": ");
      if (message) {
        return message;
      }
    }
  }
  return error instanceof Error ? error.message : String(error);
};

export default function DlqRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [error, setError] = useState<string | null>(null);
  const [mcmError, setMcmError] = useState<string | null>(null);
  const [pks, setPks] = useState<string>("");
  const [mcmId, setMcmId] = useState<string>("");
  const [createdMcmUrl, setCreatedMcmUrl] = useState<string | null>(null);
  const [mcmWarning, setMcmWarning] = useState<string | null>(null);
  const [simLink, setSimLink] = useState<string>("");
  const [athenaQuery, setAthenaQuery] = useState<string>("");
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [deleteWithoutRedriving, setDeleteWithoutRedriving] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [creatingMcm, setCreatingMcm] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showRetryConfirm, setShowRetryConfirm] = useState(false);
  const [retryConfirmText, setRetryConfirmText] = useState("");
  const [selectedExecutionArn, setSelectedExecutionArn] = useState<
    string | null
  >(null);

  // The uploaded object referenced by the MCM (persisted in localStorage).
  const [uploadedS3Uri, setUploadedS3Uri] = useState<string | null>(null);
  const [uploadedEtag, setUploadedEtag] = useState<string | null>(null);
  const [uploadedCount, setUploadedCount] = useState<number | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const executionArn = urlParams.get("executionArn");
    if (executionArn) {
      setSelectedExecutionArn(executionArn);
    }
  }, []);

  // Restore (or clear) the prepared MCM input on stage change — it's stage-specific.
  useEffect(() => {
    setPks("");
    setMcmId("");
    setCreatedMcmUrl(null);
    setMcmWarning(null);
    setMcmError(null);
    setSimLink("");
    setAthenaQuery("");
    setDryRun(false);
    setDeleteWithoutRedriving(false);
    let stored: PreparedMcm | null = null;
    try {
      const raw = localStorage.getItem(uploadStorageKey(stage));
      if (raw) {
        stored = parsePreparedMcm(raw);
      }
    } catch {
      stored = null;
    }
    if (stored?.s3Uri && stored?.etag) {
      setUploadedS3Uri(stored.s3Uri);
      setUploadedEtag(stored.etag);
      setUploadedCount(typeof stored.count === "number" ? stored.count : null);
      setMcmId(stored.mcmId ?? "");
      setCreatedMcmUrl(stored.mcmUrl ?? null);
      setMcmWarning(stored.warning ?? null);
      setDeleteWithoutRedriving(true);
    } else if (stored?.messageIds?.length) {
      setUploadedS3Uri(null);
      setUploadedEtag(null);
      setUploadedCount(null);
      setPks(stored.messageIds.join("\n"));
      setMcmId(stored.mcmId ?? "");
      setCreatedMcmUrl(stored.mcmUrl ?? null);
      setMcmWarning(stored.warning ?? null);
      setDeleteWithoutRedriving(true);
    } else {
      setUploadedS3Uri(null);
      setUploadedEtag(null);
      setUploadedCount(null);
    }
  }, [stage]);

  const {
    pastRedrives,
    loading: loadingRedrives,
    error: fetchError,
    refresh: refreshRedrives,
  } = useDlqRedriveData(stage);
  const {
    details,
    loading: loadingDetails,
    error: detailsError,
  } = useDlqRedriveDetails(stage, selectedExecutionArn);

  const {
    file,
    messageCount,
    error: fileError,
    validating: validatingFile,
    redriveOK,
    handleFileChange,
  } = useDlqFileValidation();

  useEffect(() => {
    handleFileChange([]).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  }, [stage, handleFileChange]);

  const { uploadFile, executeRedrive } = useExecuteDlqRedrive();

  // A prepared upload exists (possibly restored from localStorage, so no `file`).
  const hasPreparedUpload = deleteWithoutRedriving && !!uploadedS3Uri;
  // A file is selected but not yet uploaded — execute must wait.
  const needsUpload = deleteWithoutRedriving && !!file && !uploadedS3Uri;

  const pkList = pks.trim()
    ? pks
        .split("\n")
        .map((id) => id.trim())
        .filter((id) => id)
    : [];
  // Manual-pk path: no S3 file, so the backend embeds each pk in the MCM.
  const usingManualPks = !file && !hasPreparedUpload && pkList.length > 0;

  // Only show the steps once there's something to act on (avoids a lone Step 2).
  const showDeleteSteps =
    deleteWithoutRedriving && (!!file || hasPreparedUpload || usingManualPks);

  const persistUpload = (
    s3Uri: string,
    etag: string,
    count: number,
    createdMcm?: { id: string; url: string; warning?: string }
  ) => {
    try {
      localStorage.setItem(
        uploadStorageKey(stage),
        JSON.stringify({
          s3Uri,
          etag,
          count,
          mcmId: createdMcm?.id,
          mcmUrl: createdMcm?.url,
          warning: createdMcm?.warning,
        })
      );
    } catch {
      // Non-fatal: persistence is a convenience.
    }
  };

  const persistManualMcm = (
    messageIds: string[],
    createdMcm: { id: string; url: string; warning?: string }
  ) => {
    try {
      localStorage.setItem(
        uploadStorageKey(stage),
        JSON.stringify({
          messageIds,
          count: messageIds.length,
          mcmId: createdMcm.id,
          mcmUrl: createdMcm.url,
          warning: createdMcm.warning,
        })
      );
    } catch {
      // Non-fatal: persistence is a convenience.
    }
  };

  const clearUpload = () => {
    setUploadedS3Uri(null);
    setUploadedEtag(null);
    setUploadedCount(null);
    try {
      localStorage.removeItem(uploadStorageKey(stage));
    } catch {
      // Ignore storage errors.
    }
  };

  // Swapping the file invalidates the prepared upload (stale s3Uri/etag).
  const onFileChange = (files: File[]) => {
    setMcmId("");
    setCreatedMcmUrl(null);
    setMcmWarning(null);
    setMcmError(null);
    clearUpload();
    handleFileChange(files).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  };

  // Abandon an in-progress preparation: drop the upload, file, pks and MCM id.
  const handleDiscard = () => {
    clearUpload();
    handleFileChange([]).catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
    setPks("");
    setMcmId("");
    setCreatedMcmUrl(null);
    setMcmWarning(null);
    setMcmError(null);
    setSimLink("");
    setAthenaQuery("");
    setDryRun(false);
    setDeleteWithoutRedriving(false);
    setError(null);
  };

  // Re-run a failed execution with the same parameters (backend reuses the
  // original execution's input — works after a refresh, no re-upload).
  const handleRetry = async () => {
    if (!selectedExecutionArn) {
      return;
    }
    setRetrying(true);
    setError(null);
    try {
      const dlqRedriveApi = DlqRedriveInfoApiFactory();
      const response = await dlqRedriveApi.retryDLQRedrive(stage, {
        executionArn: selectedExecutionArn,
      });
      const newArn = response.data?.executionArn;
      if (newArn) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("executionArn", newArn);
        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${urlParams.toString()}`
        );
        setSelectedExecutionArn(newArn);
      }
    } catch (err) {
      setError(
        `Failed to retry DLQ redrive: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setRetrying(false);
      // Always close the modal — on failure this reveals the error alert that
      // would otherwise be hidden behind the open modal.
      setShowRetryConfirm(false);
    }
  };

  const handleCreateMcm = async () => {
    setCreatingMcm(true);
    setMcmError(null);
    try {
      let sourceS3Uri = uploadedS3Uri ?? undefined;
      let sourceEtag = uploadedEtag ?? undefined;
      let sourceCount = uploadedCount ?? messageCount;

      if (!usingManualPks && !sourceS3Uri) {
        if (!file) {
          throw new Error("A file is required to create this MCM");
        }
        if (!redriveOK) {
          throw new Error(fileError || "The selected file is not valid");
        }

        sourceCount = messageCount;
        const uploaded = await uploadFile(file, stage);
        if (!uploaded.etag) {
          throw new Error(
            "Upload succeeded but the S3 ETag could not be read from the response"
          );
        }

        sourceS3Uri = uploaded.s3Uri;
        sourceEtag = uploaded.etag;
        setUploadedS3Uri(sourceS3Uri);
        setUploadedEtag(sourceEtag);
        setUploadedCount(sourceCount);
        // Persist immediately so an MCM API failure can be retried without
        // re-uploading and changing the ETag recorded in the description.
        persistUpload(sourceS3Uri, sourceEtag, sourceCount);
      }

      const dlqRedriveApi = DlqRedriveInfoApiFactory();
      const response = await dlqRedriveApi.postDLQRedriveMcm(stage, {
        messageIds: usingManualPks ? pkList : undefined,
        messageIdsS3Uri: usingManualPks ? undefined : sourceS3Uri,
        messageIdsS3Etag: usingManualPks ? undefined : sourceEtag,
        simLink: simLink.trim() || undefined,
        athenaQuery: athenaQuery.trim() || undefined,
      });
      const responseData = response.data ?? {};
      const createdId = responseData.mcmId;
      if (!createdId) {
        throw new Error("The backend did not return an MCM ID");
      }
      const createdUrl =
        responseData.mcmUrl || generateMcmUrl(stage, createdId);
      const responseWarning = responseData.warning;
      const createdWarning =
        typeof responseWarning === "string" ? responseWarning : null;
      setMcmId(createdId);
      setCreatedMcmUrl(createdUrl);
      setMcmWarning(createdWarning);
      if (sourceS3Uri && sourceEtag) {
        persistUpload(sourceS3Uri, sourceEtag, sourceCount, {
          id: createdId,
          url: createdUrl,
          warning: createdWarning ?? undefined,
        });
      } else if (usingManualPks) {
        persistManualMcm(pkList, {
          id: createdId,
          url: createdUrl,
          warning: createdWarning ?? undefined,
        });
      }
    } catch (err) {
      setMcmError(getApiErrorMessage(err));
    } finally {
      setCreatingMcm(false);
    }
  };

  const handleExecute = async () => {
    if (deleteWithoutRedriving && !mcmId.trim()) {
      setError("MCM ID is required when deleteWithoutRedriving is enabled");
      return;
    }

    // The file must be uploaded first so its s3Uri/etag can go in the MCM.
    if (deleteWithoutRedriving && file && !uploadedS3Uri) {
      setError(
        "Upload the file first, then create + approve the MCM before executing"
      );
      return;
    }

    if (!pks.trim() && !file && !hasPreparedUpload) {
      setError("Either DLQ pks or a file must be provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // S3 source and inline pks are mutually exclusive — never send both.
      let messageIdsS3Uri: string | undefined;
      let messageIdsS3Etag: string | undefined;
      let messageIds: string[] | undefined;

      if (file) {
        if (deleteWithoutRedriving) {
          // Reuse the already-uploaded object — no re-upload.
          messageIdsS3Uri = uploadedS3Uri ?? undefined;
          messageIdsS3Etag = uploadedEtag ?? undefined;
        } else {
          // The file was validated immediately after selection.
          const uploaded = await uploadFile(file, stage);
          messageIdsS3Uri = uploaded.s3Uri;
          messageIdsS3Etag = uploaded.etag;
        }
      } else if (hasPreparedUpload) {
        // Upload restored from localStorage (file gone after a tab close).
        messageIdsS3Uri = uploadedS3Uri ?? undefined;
        messageIdsS3Etag = uploadedEtag ?? undefined;
      } else {
        messageIds = pkList.length > 0 ? pkList : undefined;
      }

      const result = await executeRedrive({
        stage,
        messageIds,
        messageIdsS3Uri,
        messageIdsS3Etag,
        mcmId: mcmId.trim() || undefined,
        dryRun,
        deleteWithoutRedriving,
      });

      setError(null);

      // Clear the whole wizard once a real execution has started. A later
      // failure is recoverable via Retry, so no local preparation is needed.
      // Dry runs keep the preparation for the subsequent real execution.
      if (!dryRun) {
        handleDiscard();
      }

      if (result?.executionArn) {
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set("executionArn", result.executionArn);
        window.history.pushState(
          {},
          "",
          `${window.location.pathname}?${urlParams.toString()}`
        );
        setSelectedExecutionArn(result.executionArn);
      } else {
        alert(
          `DLQ redrive ${
            dryRun ? "validation" : "started execution"
          } completed successfully`
        );
      }
    } catch (err) {
      setError(
        `Failed to execute DLQ redrive: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const hasExecutableInput = Boolean(
    pks.trim() || (file && redriveOK) || hasPreparedUpload
  );
  const canExecute =
    hasExecutableInput &&
    !needsUpload &&
    (!deleteWithoutRedriving || Boolean(mcmId.trim()));
  const draftMcmCreated = Boolean(mcmId && createdMcmUrl);
  const executeButtonText = dryRun
    ? "Validate Redrive"
    : deleteWithoutRedriving
    ? "Execute DLQ Deletion"
    : "Execute Redrive";
  const combinedError =
    [error, fetchError, detailsError].filter(Boolean).join("; ") || null;
  const runAsyncAction = (action: () => Promise<void>) => {
    action().catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
  };
  const handleBackToRedrives = () => {
    setSelectedExecutionArn(null);
    refreshRedrives().catch((unexpectedError: unknown) => {
      setError(`Unexpected error: ${getApiErrorMessage(unexpectedError)}`);
    });
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete("executionArn");
    window.history.pushState(
      {},
      "",
      `${window.location.pathname}?${urlParams.toString()}`
    );
  };

  if (selectedExecutionArn) {
    return (
      <DlqRedriveExecutionView
        combinedError={combinedError}
        details={details}
        loadingDetails={loadingDetails}
        onBack={handleBackToRedrives}
        onRetry={() => runAsyncAction(handleRetry)}
        onRetryConfirmTextChange={setRetryConfirmText}
        onRetryDismiss={() => setShowRetryConfirm(false)}
        onRetryOpen={() => {
          setRetryConfirmText("");
          setShowRetryConfirm(true);
        }}
        retryConfirmText={retryConfirmText}
        retrying={retrying}
        selectedExecutionArn={selectedExecutionArn}
        showRetryConfirm={showRetryConfirm}
        stage={stage}
      />
    );
  }

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {combinedError && (
            <ErrorAlert content={combinedError} header="DLQ Redrive Error" />
          )}

          <Container header={<Box variant="h2">DLQ Redrive Tool</Box>}>
            <SpaceBetween size="m">
              <Box variant="p">
                This tool allows you to redrive messages from the Dead Letter
                Queue (DLQ). You can provide DLQ pks directly or upload a CSV
                file containing DLQ pks. Use dry run mode to validate without
                actually redriving messages.
              </Box>
              {!selectedExecutionArn && (
                <StageSelector stage={stage} setStage={setStage} />
              )}
            </SpaceBetween>
          </Container>

          {!selectedExecutionArn && (
            <Container>
              <SpaceBetween size="m">
                <Box variant="h2">DLQ Redrive Configuration</Box>

                <FormField
                  label="DLQ pks"
                  description="Enter DLQ pks (primary keys), one per line. Leave empty if uploading a file."
                >
                  <Textarea
                    value={pks}
                    onChange={({ detail }) => {
                      clearUpload();
                      setPks(detail.value);
                      setMcmId("");
                      setCreatedMcmUrl(null);
                      setMcmWarning(null);
                      setMcmError(null);
                    }}
                    placeholder="pk-1&#10;pk-2&#10;pk-3"
                    rows={5}
                    disabled={!!file || hasPreparedUpload}
                  />
                </FormField>

                <Box variant="p">OR</Box>

                <FileUploader
                  file={file}
                  handleFileChange={onFileChange}
                  redriveOK={false}
                  loading={loading}
                  fileError={fileError}
                  expandedFileToken
                />
                {file && (validatingFile || redriveOK) && (
                  <div>
                    {validatingFile && (
                      <StatusIndicator type="loading">
                        Validating file...
                      </StatusIndicator>
                    )}
                    {redriveOK && (
                      <StatusIndicator type="success">
                        File valid ({messageCount} DLQ{" "}
                        {messageCount === 1 ? "pk" : "pks"})
                      </StatusIndicator>
                    )}
                  </div>
                )}

                <SpaceBetween size="xs">
                  <Checkbox
                    checked={dryRun}
                    onChange={({ detail }) => setDryRun(detail.checked)}
                  >
                    Dry Run (validate without executing)
                  </Checkbox>

                  <Checkbox
                    checked={deleteWithoutRedriving}
                    onChange={({ detail }) => {
                      setDeleteWithoutRedriving(detail.checked);
                      setMcmId("");
                      setCreatedMcmUrl(null);
                      setMcmWarning(null);
                      setMcmError(null);
                      if (!detail.checked) {
                        clearUpload();
                      }
                    }}
                  >
                    Delete Without Redriving
                  </Checkbox>
                </SpaceBetween>

                <DlqRedriveApprovalFlow
                  athenaQuery={athenaQuery}
                  canExecute={canExecute}
                  createdMcmUrl={createdMcmUrl}
                  creatingMcm={creatingMcm}
                  disableCreateDraftMcm={!!file && !redriveOK}
                  draftMcmCreated={draftMcmCreated}
                  executeButtonText={executeButtonText}
                  loading={loading}
                  mcmError={mcmError}
                  mcmId={mcmId}
                  mcmWarning={mcmWarning}
                  onAthenaQueryChange={setAthenaQuery}
                  onCreateDraftMcm={() => runAsyncAction(handleCreateMcm)}
                  onDiscard={handleDiscard}
                  onExecute={() => runAsyncAction(handleExecute)}
                  onSimLinkChange={setSimLink}
                  pkList={pkList}
                  showDeleteSteps={showDeleteSteps}
                  simLink={simLink}
                  uploadedEtag={uploadedEtag}
                  uploadedS3Uri={uploadedS3Uri}
                  usingManualPks={usingManualPks}
                />
              </SpaceBetween>
            </Container>
          )}

          <DlqExecutionsTable items={pastRedrives} loading={loadingRedrives} />
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
}
